import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpDown, Globe, Mail, MessageCircle, Phone, Search } from "lucide-react";
import { authHeaders } from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "necontactat", label: "Necontactat", color: "#6b7280" },
  { value: "contactat", label: "Contactat", color: "#24435C" },
  { value: "ofertat", label: "Ofertat", color: "#b45309" },
  { value: "finalizat", label: "Finalizat", color: "#15803d" },
  { value: "incasat", label: "Încasat", color: "#7c3aed" },
];

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

const waPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (d.startsWith("07")) return `4${d}`;
  if (d.startsWith("40")) return d;
  return d;
};

const selectCls =
  "border border-ink/15 bg-surface px-3.5 py-2.5 text-xs font-medium text-ink outline-none transition-colors focus:border-gold";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("toate");
  const [year, setYear] = useState("toti");
  const [month, setMonth] = useState("toate");
  const [sort, setSort] = useState("noi");
  const [query, setQuery] = useState("");

  const load = () => {
    axios
      .get(`${API}/admin/leads`, { headers: authHeaders() })
      .then(({ data }) => setLeads(data))
      .catch(() => toast.error("Nu am putut încărca lead-urile."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setStatus = async (lead, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { status }, { headers: authHeaders() });
      setLeads(leads.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Status actualizat: ${STATUSES.find((s) => s.value === status)?.label}`);
    } catch {
      toast.error("Nu am putut actualiza statusul.");
    }
  };

  const years = useMemo(
    () => [...new Set(leads.map((l) => l.created_at.slice(0, 4)))].sort().reverse(),
    [leads]
  );

  const months = useMemo(() => {
    const inYear = leads.filter((l) => year === "toti" || l.created_at.startsWith(year));
    return [...new Set(inYear.map((l) => l.created_at.slice(5, 7)))].sort();
  }, [leads, year]);

  const counts = useMemo(() => {
    const c = { toate: leads.length };
    STATUSES.forEach((s) => {
      c[s.value] = leads.filter((l) => (l.status || "necontactat") === s.value).length;
    });
    return c;
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = leads.filter((l) => {
      if (statusFilter !== "toate" && (l.status || "necontactat") !== statusFilter) return false;
      if (year !== "toti" && !l.created_at.startsWith(year)) return false;
      if (month !== "toate" && l.created_at.slice(5, 7) !== month) return false;
      if (q && ![l.name, l.email, l.phone, l.company_name, l.subject, l.county, l.country_name, l.country_code, l.source]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))) return false;
      return true;
    });
    if (sort === "vechi") list = [...list].sort((a, b) => a.created_at.localeCompare(b.created_at));
    else if (sort === "nume") list = [...list].sort((a, b) => a.name.localeCompare(b.name, "ro"));
    else list = [...list].sort((a, b) => b.created_at.localeCompare(a.created_at));
    return list;
  }, [leads, statusFilter, year, month, sort, query]);

  if (loading) return <p className="kicker">Se încarcă lead-urile...</p>;

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="kicker">Administrare</p>
          <h1 className="mt-3 font-heading text-3xl font-semibold md:text-4xl">Cereri de ofertă primite</h1>
          <p className="mt-2 text-sm text-ink/55">
            Toate mesajele din formularul de contact și din wizard-ul de pe homepage.
          </p>
        </div>
        <p className="font-heading text-lg italic text-ink/45" data-testid="admin-leads-count">
          {filtered.length} din {leads.length}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3 lg:grid-cols-6" data-testid="leads-stats">
        <button
          onClick={() => setStatusFilter("toate")}
          data-testid="stat-toate"
          className={`bg-surface p-5 text-left transition-colors hover:bg-base ${statusFilter === "toate" ? "shadow-[inset_0_-3px_0_0_rgb(var(--c-gold))]" : ""}`}
        >
          <p className="font-heading text-3xl font-semibold">{counts.toate}</p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">Toate</p>
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(statusFilter === s.value ? "toate" : s.value)}
            data-testid={`stat-${s.value}`}
            className={`bg-surface p-5 text-left transition-colors hover:bg-base ${statusFilter === s.value ? "shadow-[inset_0_-3px_0_0_rgb(var(--c-gold))]" : ""}`}
          >
            <p className="flex items-center gap-2 font-heading text-3xl font-semibold">
              {counts[s.value]}
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            </p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">{s.label}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="leads-toolbar">
        <div className="relative min-w-52 flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/35" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Caută după nume, email, firmă, subiect..."
            data-testid="leads-search"
            className="w-full border border-ink/15 bg-surface py-2.5 pl-10 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-gold"
          />
        </div>
        <select value={year} onChange={(e) => { setYear(e.target.value); setMonth("toate"); }} data-testid="leads-year" className={selectCls}>
          <option value="toti">Toți anii</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="leads-month" className={selectCls}>
          <option value="toate">Toate lunile</option>
          {months.map((m) => (
            <option key={m} value={m}>{MONTHS_RO[parseInt(m, 10) - 1]}</option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="leads-sort" className={selectCls}>
          <option value="noi">Cele mai noi</option>
          <option value="vechi">Cele mai vechi</option>
          <option value="nume">Nume A–Z</option>
        </select>
        <ArrowUpDown className="h-4 w-4 text-ink/30" aria-hidden />
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8 border border-ink/10 bg-surface p-12 text-center" data-testid="admin-leads-empty">
          <p className="font-heading text-xl">Nicio cerere pe filtrul ales</p>
          <p className="mt-2 text-sm text-ink/55">Schimbă filtrele sau revino mai târziu.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {filtered.map((lead) => {
            const st = STATUSES.find((s) => s.value === lead.status) || STATUSES[0];
            const wa = waPhone(lead.phone);
            return (
              <div key={lead.id} className="border border-ink/10 bg-surface p-6" data-testid={`lead-card-${lead.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-xl font-semibold" data-testid="lead-name">{lead.name}</h3>
                    <p className="mt-1 text-xs uppercase tracking-widest text-ink/45">
                      {lead.subject}{lead.county ? ` · ${lead.county}` : ""} · {new Date(lead.created_at).toLocaleString("ro-RO")}
                    </p>
                    {(lead.country_code || lead.lang || lead.source) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`lead-origin-${lead.id}`}>
                        {lead.country_code && (
                          <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] ${lead.country_code !== "RO" ? "border-gold/50 bg-gold/10 text-gold" : "border-ink/15 text-ink/60"}`}>
                            <Globe className="h-3 w-3" /> {lead.country_code}{lead.country_name && lead.country_code !== "RO" ? ` · ${lead.country_name}` : ""}{lead.city ? ` · ${lead.city}` : ""}
                          </span>
                        )}
                        {lead.lang && (
                          <span className="border border-ink/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/60">{lead.lang}</span>
                        )}
                        {lead.source && (
                          <span className="border border-ink/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/60">{lead.source}</span>
                        )}
                        {lead.page && (
                          <span className="px-1 text-[10px] text-ink/40">{lead.page}</span>
                        )}
                      </div>
                    )}
                    {lead.company_name && (
                      <p className="mt-1.5 text-sm font-medium text-ink/70">
                        {lead.company_name} {lead.cui && <span className="text-ink/45">· CUI {lead.cui}</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: st.color }} />
                    <select
                      value={lead.status || "necontactat"}
                      onChange={(e) => setStatus(lead, e.target.value)}
                      data-testid={`lead-status-${lead.id}`}
                      className="border border-ink/15 bg-base px-3 py-2 text-xs font-semibold uppercase tracking-widest text-ink outline-none focus:border-gold"
                    >
                      {STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {lead.message && (
                  <p className="mt-4 max-w-3xl text-sm leading-relaxed text-ink/65">{lead.message}</p>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-ink/10 pt-4">
                  {wa && (
                    <a
                      href={`https://wa.me/${wa}?text=${encodeURIComponent(`Bună ziua, ${lead.name}! Vă contactăm de la HEAVY METAL referitor la cererea dvs. (${lead.subject}).`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`lead-wa-${lead.id}`}
                      className="flex items-center gap-2 border border-ink/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-ink/70 transition-colors hover:border-[#25D366] hover:text-[#25D366]"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                  {lead.phone && (
                    <a
                      href={`tel:${wa}`}
                      data-testid={`lead-call-${lead.id}`}
                      className="flex items-center gap-2 border border-ink/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-ink/70 transition-colors hover:border-gold hover:text-gold"
                    >
                      <Phone className="h-3.5 w-3.5" /> {lead.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${lead.email}?subject=${encodeURIComponent(`Ofertă HEAVY METAL — ${lead.subject}`)}`}
                    data-testid={`lead-email-${lead.id}`}
                    className="flex items-center gap-2 border border-ink/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-ink/70 transition-colors hover:border-gold hover:text-gold"
                  >
                    <Mail className="h-3.5 w-3.5" /> {lead.email}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
